import { SelectionModel } from '@angular/cdk/collections';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Input,
   OnDestroy,
   OnInit,
   ViewEncapsulation,
} from '@angular/core';
import {
   ControlValueAccessor,
   FormGroupDirective,
   NG_VALUE_ACCESSOR,
   UntypedFormBuilder,
   UntypedFormControl,
   UntypedFormGroup,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from 'src/app/common-components/confirmation-dialog/models/confirmation-dialog.config';
import { ExtensionGroupOption } from '../extension-groups-register/models/extension-group-option.interface';
import { ExtensionOption } from '../extension-register/models/extension-option.interface';
import { GetUserDetail_user_rolesConnection_items } from '../user-details/queries/generated/GetUserDetail';
import {
   RecordingGroupPermission,
   RecordingPermission,
   RecordingPermissions,
} from './models/recording-permissions.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-call-recording-extensions-list',
    styleUrls: ['./call-recording-extensions-list.component.scss'],
    templateUrl: './call-recording-extensions-list.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: CallRecordingExtensionsListComponent, multi: true }],
    standalone: false
})
export class CallRecordingExtensionsListComponent implements ControlValueAccessor, OnDestroy, OnInit {
   @Input() extensions: ExtensionOption[];

   @Input() extensionGroups: ExtensionGroupOption[];

   get permissions(): RecordingPermissions {
      return this._permissions;
   }

   set permissions(value: RecordingPermissions) {
      this._permissions = value;
      this.filteredExcludePermissions = value.extensionRecordingPermissions;
      this.filteredIncludePermissions = value.extensionRecordingPermissions;

      this.filteredIncludeGroupPermissions = value.extensionGroupRecordingPermissions;
      this.filteredExcludeGroupPermissions = value.extensionGroupRecordingPermissions;
   }

   public roleOverrideCheckboxes = new Map<number, { include: boolean; exclude: boolean; roleNames: string[] }>();

   private _userRoles: GetUserDetail_user_rolesConnection_items[] = [];

   @Input()
   set userRoles(userRoles: GetUserDetail_user_rolesConnection_items[]) {
      if (!userRoles?.length) {
         this._userRoles = [];
         this.roleOverrideCheckboxes.clear();
         this.changeDetectorRef.markForCheck();
         return;
      }

      this._userRoles = userRoles;
      this.roleOverrideCheckboxes.clear();

      this.setupRoleOverrideMap(userRoles);

      this.changeDetectorRef.markForCheck();
   }

   get userRoles(): GetUserDetail_user_rolesConnection_items[] {
      return this._userRoles;
   }

   private initialSelectedPermissions = new Set<number>();

   private initialSelectedGroupPermissions = new Set<number>();

   private initialSelectedExcludedPermissions = new Set<number>();

   private initialSelectedExcludedGroupPermissions = new Set<number>();

   filteredIncludePermissions: RecordingPermission[];

   filteredExcludePermissions: RecordingPermission[];

   filteredIncludeGroupPermissions: RecordingGroupPermission[];

   filteredExcludeGroupPermissions: RecordingGroupPermission[];

   permissionSelection: SelectionModel<RecordingPermission>;

   excludePermissionSelection: SelectionModel<RecordingPermission>;

   permissionGroupSelection: SelectionModel<RecordingGroupPermission>;

   excludePermissionGroupSelection: SelectionModel<RecordingGroupPermission>;

   form: UntypedFormGroup;

   includeSearchText: UntypedFormControl;

   excludeSearchText: UntypedFormControl;

   private dialogIsOpen = false;

   formDisabled: boolean;

   onChange: (value: RecordingPermissions) => void;

   onTouched: () => void;

   private destroyed$ = new Subject<void>();

   private dirtyExtensionPermissions = new Set<number>();

   private dirtyExtensionGroupPermissions = new Set<number>();

   private _permissions: RecordingPermissions;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      formBuilder: UntypedFormBuilder,
      private confirmationService: ConfirmationDialogService,
      private fg: FormGroupDirective
   ) {
      this.includeSearchText = formBuilder.control('');
      this.excludeSearchText = formBuilder.control('');
      this.form = formBuilder.group({
         includeSearchText: this.includeSearchText,
         excludeSearchText: this.excludeSearchText,
      });
      this.fg.ngSubmit.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.form.patchValue({
            includeSearchText: '',
            excludeSearchText: '',
         });
      });

      this.permissionSelection = new SelectionModel<RecordingPermission>(true, []);
      this.excludePermissionSelection = new SelectionModel<RecordingPermission>(true, []);
      this.permissionGroupSelection = new SelectionModel<RecordingGroupPermission>(true, []);
      this.excludePermissionGroupSelection = new SelectionModel<RecordingGroupPermission>(true, []);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   ngOnInit(): void {
      this.form.get('includeSearchText')?.valueChanges.subscribe((searchText: string) => {
         this.filteredIncludePermissions = this.filterPermissions(
            this.permissions.extensionRecordingPermissions,
            searchText || ''
         );

         this.filteredIncludeGroupPermissions = this.filterGroupPermissions(
            this.permissions.extensionGroupRecordingPermissions,
            searchText || ''
         );
      });

      this.form.get('excludeSearchText')?.valueChanges.subscribe((searchText: string) => {
         this.filteredExcludePermissions = this.filterPermissions(
            this.permissions.extensionRecordingPermissions,
            searchText || ''
         );

         this.filteredExcludeGroupPermissions = this.filterGroupPermissions(
            this.permissions.extensionGroupRecordingPermissions,
            searchText || ''
         );
      });
   }

   writeValue(permissions: RecordingPermissions): void {
      if (permissions) {
         this.permissionSelection.clear();
         this.excludePermissionSelection.clear();

         this.permissionGroupSelection.clear();
         this.excludePermissionGroupSelection.clear();

         this.dirtyExtensionPermissions.clear();
         this.dirtyExtensionGroupPermissions.clear();

         this.initialSelectedPermissions.clear();
         this.initialSelectedGroupPermissions.clear();
         this.initialSelectedExcludedPermissions.clear();
         this.initialSelectedExcludedGroupPermissions.clear();

         this.permissions = permissions;
         for (const permission of permissions.extensionRecordingPermissions.filter(
            (permission) => permission.id !== 0
         )) {
            if (permission.isInclude) {
               this.permissionSelection.select(permission);
               this.initialSelectedPermissions.add(permission.extension.extensionId);
            } else {
               this.excludePermissionSelection.select(permission);
               this.initialSelectedExcludedPermissions.add(permission.extension.extensionId);
            }
         }

         for (const permission of permissions.extensionGroupRecordingPermissions.filter(
            (permission) => permission.id !== 0
         )) {
            if (permission.isInclude) {
               this.permissionGroupSelection.select(permission);
               this.initialSelectedGroupPermissions.add(permission.extensionGroup.extensionGroupId);
            } else {
               this.excludePermissionGroupSelection.select(permission);
               this.initialSelectedExcludedGroupPermissions.add(permission.extensionGroup.extensionGroupId);
            }
         }
         this.changeDetectorRef.markForCheck();
      }
   }

   registerOnChange(onChange: (values: RecordingPermissions | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   selectAllCheckedChanged(checkboxChange: MatCheckboxChange) {
      this.permissions.allExtensionsIsSelectedDirty = true;
      this.permissions.allExtensionsIsSelected = checkboxChange.checked;
      this.handleChange();
   }

   toggleExtension(permission: RecordingPermission) {
      this.toggle(permission, this.permissionSelection);
   }

   toggleExtensionGroup(permission: RecordingGroupPermission) {
      this.toggleGroup(permission, this.permissionGroupSelection);
   }

   toggleExcludeExtension(permission: RecordingPermission) {
      this.toggle(permission, this.excludePermissionSelection);
   }

   toggleExcludeExtensionGroup(permission: RecordingGroupPermission) {
      this.toggleGroup(permission, this.excludePermissionGroupSelection);
   }

   setDisabledState(isDisabled: boolean): void {
      this.formDisabled = isDisabled;
   }

   private setupRoleOverrideMap(userRoles: GetUserDetail_user_rolesConnection_items[]) {
      for (const userRole of userRoles) {
         for (const extensionRecordingPermission of userRole.role.roleCallRecordingExtensionsConnection.items) {
            const permission = extensionRecordingPermission.isInclude
               ? { include: true, exclude: false }
               : { include: false, exclude: true };
            const extensionId = extensionRecordingPermission.extension.extensionId;
            const roleNames = this.roleOverrideCheckboxes.has(extensionId)
               ? [...(this.roleOverrideCheckboxes.get(extensionId)?.roleNames ?? []), userRole.role.name ?? '']
               : [userRole.role.name ?? ''];

            this.roleOverrideCheckboxes.set(extensionId, { ...permission, roleNames });
         }

         for (const extensionRecordingPermission of userRole.role.roleCallRecordingExtensionGroupsConnection.items) {
            const permission = extensionRecordingPermission.isInclude
               ? { include: true, exclude: false }
               : { include: false, exclude: true };
            const extensionGroupId = extensionRecordingPermission.extensionGroupId;
            const roleNames = this.roleOverrideCheckboxes.has(extensionGroupId)
               ? [...(this.roleOverrideCheckboxes.get(extensionGroupId)?.roleNames ?? []), userRole.role.name ?? '']
               : [userRole.role.name ?? ''];

            this.roleOverrideCheckboxes.set(extensionGroupId, { ...permission, roleNames });
         }

         if (userRole.role.hasAllExtensionRecordings) {
            const roleNames = this.roleOverrideCheckboxes.has(0)
               ? [...(this.roleOverrideCheckboxes.get(0)?.roleNames ?? []), userRole.role.name ?? '']
               : [userRole.role.name ?? ''];
            this.roleOverrideCheckboxes.set(0, { include: true, exclude: false, roleNames });
            this.permissions.allExtensionsIsSelected = true;
         }
      }
   }

   private toggle(permission: RecordingPermission, selection: SelectionModel<RecordingPermission>) {
      selection.toggle(permission);
      this.dirtyExtensionPermissions.add(permission.extension.extensionId);
      this.handleChange();
   }

   private toggleGroup(permission: RecordingGroupPermission, selection: SelectionModel<RecordingGroupPermission>) {
      selection.toggle(permission);
      this.dirtyExtensionGroupPermissions.add(permission.extensionGroup.extensionGroupId);
      this.handleChange();
   }

   private handleChange() {
      if (!this.permissions) {
         return;
      }

      const updatedValue: RecordingPermissions = {
         allExtensionsIsSelected: this.permissions.allExtensionsIsSelected,
         allExtensionsIsSelectedDirty: this.permissions.allExtensionsIsSelectedDirty,
         extensionRecordingPermissions: this.permissions.extensionRecordingPermissions
            .filter((p) => {
               if (
                  this.dirtyExtensionPermissions.has(p.extension.extensionId) &&
                  !this.permissionIsSelected(p) &&
                  !this.initiallySelectedPermission(p)
               ) {
                  this.dirtyExtensionPermissions.delete(p.extension.extensionId);
               }
               return (
                  (this.permissions.allExtensionsIsSelectedDirty && p.id !== 0) ||
                  this.dirtyExtensionPermissions.has(p.extension.extensionId)
               );
            })
            .map((permission): RecordingPermission => {
               return {
                  extension: permission.extension,
                  id: permission.id,
                  isInclude:
                     !this.excludePermissionSelection.isSelected(permission) &&
                     this.permissionSelection.isSelected(permission),
                  isDeleted: this.checkIfPrivilegeShouldBeDeleted(permission),
               };
            }),
         extensionGroupRecordingPermissions: this.permissions.extensionGroupRecordingPermissions
            .filter((p) => {
               if (
                  this.dirtyExtensionGroupPermissions.has(p.extensionGroup.extensionGroupId) &&
                  !this.permissionGroupIsSelected(p) &&
                  !this.initiallySelectedGroupPermission(p)
               ) {
                  this.dirtyExtensionGroupPermissions.delete(p.extensionGroup.extensionGroupId);
               }
               return (
                  (this.permissions.allExtensionsIsSelectedDirty && p.id !== 0) ||
                  this.dirtyExtensionGroupPermissions.has(p.extensionGroup.extensionGroupId)
               );
            })
            .map((permission): RecordingGroupPermission => {
               return {
                  extensionGroup: permission.extensionGroup,
                  id: permission.id,
                  isInclude:
                     !this.excludePermissionGroupSelection.isSelected(permission) &&
                     this.permissionGroupSelection.isSelected(permission),
                  isDeleted: this.checkIfPrivilegeGroupShouldBeDeleted(permission),
               };
            }),
      };
      this.onChange(updatedValue);
   }

   private permissionIsSelected(p: RecordingPermission): boolean {
      return this.excludePermissionSelection.isSelected(p) || this.permissionSelection.isSelected(p);
   }

   private permissionGroupIsSelected(p: RecordingGroupPermission): boolean {
      return this.excludePermissionGroupSelection.isSelected(p) || this.permissionGroupSelection.isSelected(p);
   }

   private initiallySelectedPermission(p: RecordingPermission): boolean {
      return (
         this.initialSelectedPermissions.has(p.extension.extensionId) ||
         this.initialSelectedExcludedPermissions.has(p.extension.extensionId)
      );
   }

   private initiallySelectedGroupPermission(p: RecordingGroupPermission): boolean {
      return (
         this.initialSelectedGroupPermissions.has(p.extensionGroup.extensionGroupId) ||
         this.initialSelectedExcludedGroupPermissions.has(p.extensionGroup.extensionGroupId)
      );
   }

   private filterPermissions(extensions: RecordingPermission[], searchText: string) {
      searchText = searchText.toLowerCase();
      if (searchText.length === 0) {
         return extensions;
      }
      return extensions.filter((e) => {
         const name = e.extension.name?.toLowerCase();
         const number = e.extension.number?.toString().toLowerCase();
         return name?.includes(searchText) || number?.includes(searchText);
      });
   }

   private filterGroupPermissions(extensionGroups: RecordingGroupPermission[], searchText: string) {
      searchText = searchText.toLowerCase();
      if (searchText.length === 0) {
         return extensionGroups;
      }
      return extensionGroups.filter((e) => {
         const name = e.extensionGroup.name?.toLowerCase();
         const number = e.extensionGroup.extensionGroupId?.toString().toLowerCase();
         return name?.includes(searchText) || number?.includes(searchText);
      });
   }

   private checkIfPrivilegeShouldBeDeleted(permission: RecordingPermission): boolean {
      const wasCheckedCondition =
         this.initialSelectedPermissions.has(permission.extension.extensionId) ||
         this.initialSelectedExcludedPermissions.has(permission.extension.extensionId);
      return (
         wasCheckedCondition &&
         !this.permissionSelection.isSelected(permission) &&
         !this.excludePermissionSelection.isSelected(permission)
      );
   }
   private checkIfPrivilegeGroupShouldBeDeleted(permission: RecordingGroupPermission): boolean {
      const wasCheckedCondition =
         this.initialSelectedGroupPermissions.has(permission.extensionGroup.extensionGroupId) ||
         this.initialSelectedExcludedGroupPermissions.has(permission.extensionGroup.extensionGroupId);
      return (
         wasCheckedCondition &&
         !this.permissionGroupSelection.isSelected(permission) &&
         !this.excludePermissionGroupSelection.isSelected(permission)
      );
   }

   showPopup(permission) {
      const rolesListString = permission.roleNames.join('\n');
      const message = `This permission is granted by a role. User must be removed from the following role(s) to remove the permission: \n\n ${rolesListString}`;

      if (!this.dialogIsOpen) {
         this.openDialog(message);
      }
      this.changeDetectorRef.markForCheck();
   }

   openDialog(message: string): void {
      this.dialogIsOpen = true;

      const dialogRef = this.confirmationService.confirm({
         data: {
            dialogType: ConfirmationDialogType.Ok,
            header: 'Could not remove privilege',
            message: message,
         },
         size: DialogSize.Medium,
      });

      dialogRef.afterClosed().subscribe(() => {
         this.dialogIsOpen = false;
      });
   }

   isClickable(permission): boolean | undefined {
      const roleOverride = this.roleOverrideCheckboxes.get(permission.extension.extensionId);
      return roleOverride?.include && !this.permissions?.allExtensionsIsSelected;
   }

   isClickableGroup(permission): boolean | undefined {
      const roleOverride = this.roleOverrideCheckboxes.get(permission.extensionGroup.extensionGroupId);
      return roleOverride?.include && !this.permissions?.allExtensionsIsSelected;
   }

   isDisabled(permission, isExclude = false): boolean | undefined {
      const roleOverride = this.roleOverrideCheckboxes.get(permission.extension.extensionId);
      return (
         this.formDisabled ||
         (isExclude ? roleOverride?.exclude : this.permissions?.allExtensionsIsSelected || roleOverride?.include)
      );
   }

   isChecked(permission, isExclude = false): boolean | undefined {
      const roleOverride = this.roleOverrideCheckboxes.get(permission.extension.extensionId);
      return isExclude
         ? this.excludePermissionSelection.isSelected(permission) || roleOverride?.exclude
         : this.permissionSelection.isSelected(permission) || roleOverride?.include;
   }

   isCheckedGroup(permission, isExclude = false): boolean | undefined {
      const roleOverride = this.roleOverrideCheckboxes.get(permission.extensionGroup.extensionGroupId);
      return isExclude
         ? this.excludePermissionGroupSelection.isSelected(permission) || roleOverride?.exclude
         : this.permissionGroupSelection.isSelected(permission) || roleOverride?.include;
   }
}
